<style lang="scss">
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/utils";
  @use "../../../assets/css/design-system/mixins";

  label {
    all: unset;
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .label-text {
      padding: 0 utils.in_rem(4px) utils.in_rem(4px) utils.in_rem(4px);

      &.large {
        @include mixins.body2();
      }

      &.small {
        @include mixins.body3();
      }
    }

    .wrapper {
      width: 100%;
      border: 1px solid colors.get(interfaceSwGrey_10);
      border-radius: 10px;
      display: flex;
      flex: 1;
      flex-direction: row;
      align-items: center;
      position: relative;

      &:hover {
        border: 1px solid colors.get(primary_10);
      }

      &:focus-within {
        outline: 1px solid colors.get(primary_40);
      }

      &.error {
        outline: 1px solid colors.get(systemError_100);
      }

      &.warning {
        outline: 1px solid colors.get(systemWarning_100);
      }
    }

    input,
    textarea {
      width: 100%;
      height: utils.in_rem(44px);
      background: transparent;
      border: none;
      border-radius: 10px;
      padding: utils.in_rem(12px);

      &:focus {
        outline: none;
      }

      @include mixins.body2();
      @include colors.color(black_90);

      &::placeholder {
        @include colors.color(textDisabledGrey);
      }
    }

    input {
      &.xsmall {
        @include mixins.body3();
        height: utils.in_rem(32px);
        padding: utils.in_rem(8px);
        border-radius: 8px;
      }

      &.small {
        height: utils.in_rem(36px);
        border-radius: 8px;
      }

      &.large {
        height: utils.in_rem(44px);
      }
    }

    .leading-icon {
      display: flex;
      align-items: center;
      color: colors.get(black_50);
      margin-left: 0.75rem;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
    }

    .right-slot {
      display: flex;
      align-items: center;
      color: colors.get(black_50);
      margin-right: 0.75rem;
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
    }

    textarea {
      resize: none;
    }

    .helper-text {
      @include colors.color(textGrey);
      @include mixins.caption();
      display: flex;
      flex-direction: row;
      gap: utils.in_rem(4px);
      padding: utils.in_rem(8px) 0 0 utils.in_rem(4px);

      &.error {
        @include colors.color(systemError_100);
      }

      &.warning {
        @include colors.color(systemWarning_100);
      }
    }
  }
</style>

<script>
  import { onMount } from "svelte";
  import IconScale from "../../atoms/IconScale.svelte";

  let cls = "";
  export { cls as class };
  export let value = "";
  /** @type {"textarea" | "input"} */
  export let kind = "input";
  export let size = "large";
  export let placeholder = "";
  export let state = "";
  export let readonly = false;
  export let autoFocus = false;
  export let isLoading = false; // Loading state prop

  // Icon props
  export let leadingIcon = ""; // FontAwesome icon class for left/leading icon
  export let trailingIcon = ""; // FontAwesome icon class for right/trailing icon
  export let trailingImage = null; // Image URL to display on the right
  export let trailingImageAlt = ""; // Alt text for the trailing image

  // Style props
  export let style = ""; /** Label style prop - will be renamed accordingly. */
  export let wrapperStyle = "";
  export let textareaStyle = "";

  // Autofocus
  let inputElement;

  export const blur = () => {
    inputElement?.blur();
  };

  onMount(() => {
    if (autoFocus && inputElement) inputElement.focus();
  });

  // Props for the label above the text input
  export let label = "";
  export let labelSize = "small";

  // Props for the helper text below the dropdown
  export let helperText = "";
</script>

<label {style} class={cls}>
  {#if label}
    <span class="label-text {labelSize}">{label}</span>
  {/if}
  <div class="wrapper {state}" style={wrapperStyle}>
    {#if kind === "input"}
      <input
        bind:this={inputElement}
        class="{state} {size}"
        type="text"
        bind:value
        {placeholder}
        {readonly}
        on:keydown
        on:input
        on:focus
        on:blur
      />
    {:else}
      <textarea
        bind:this={inputElement}
        class={state}
        type="text"
        bind:value
        {placeholder}
        {readonly}
        style={textareaStyle}
        on:keydown
        on:input
        on:focus
        on:blur
      ></textarea>
    {/if}
    {#if leadingIcon}
      <div class="leading-icon">
        <IconScale class={leadingIcon} size="small" />
      </div>
    {/if}

    {#if isLoading}
      <div class="right-slot">
        <i class="fa-solid fa-spinner fa-spin"></i>
      </div>
    {:else if trailingIcon}
      <div class="right-slot">
        <IconScale class={trailingIcon} size="small" />
      </div>
    {:else if trailingImage}
      <div class="right-slot">
        <img
          src={trailingImage}
          alt={trailingImageAlt}
          style="height: 24px; max-width: 100px; object-fit: contain;"
        />
      </div>
    {:else if $$slots.right}
      <slot name="right" />
    {/if}
  </div>
  {#if helperText}
    <span class="helper-text {state}">
      {#if ["error", "warning"].includes(state)}
        <IconScale class="fa-solid fa-circle-info" size="xs" />
      {/if}
      {helperText}
    </span>
  {/if}
</label>
